import { groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import {
  getScanPart,
  getScanPhotosWithoutPhotoId,
  getScanWithUpdatedScanPart,
  getUniqueScanPhotos,
} from '@kaiizen/kai-scan-template-system';
import type {
  ScanPartContext,
  ScanPhoto,
  ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

export type GalleryPhotoOccurrence = {
  key: string;
  photo: ScanPhoto;
  context: ScanPartContext;
  sectionTitle: string;
};

export type GalleryPhotoTarget = {
  key: string;
  context: ScanPartContext;
  title: string;
  subtitle?: string;
  depth: 0 | 1;
};

export type GalleryPhotoOperation =
  | { type: 'remove'; sources: readonly GalleryPhotoOccurrence[] }
  | { type: 'copy'; sources: readonly GalleryPhotoOccurrence[]; target: ScanPartContext }
  | { type: 'move'; sources: readonly GalleryPhotoOccurrence[]; target: ScanPartContext }
  | { type: 'setDate'; sources: readonly GalleryPhotoOccurrence[]; addedAt: number };

const getGalleryPhotoContextKey = ({
  groupId,
  taskId,
  optionId,
  lineItemId,
}: ScanPartContext): string => JSON.stringify([groupId, taskId, optionId, lineItemId]);

export const contextsAreEqual = (first: ScanPartContext, second: ScanPartContext): boolean =>
  getGalleryPhotoContextKey(first) === getGalleryPhotoContextKey(second);

const getGalleryScanPart = (scanRoot: ScanRootOptimized, context: ScanPartContext) =>
  getScanPart(
    scanRoot,
    context.groupId,
    context.taskId,
    context.optionId,
    context.lineItemId,
  );

const getGalleryScanPartOrUndefined = (
  scanRoot: ScanRootOptimized,
  { groupId, taskId, optionId, lineItemId }: ScanPartContext,
) => {
  if (!groupId) return scanRoot;
  const group = scanRoot.groupsById[groupId];
  if (!taskId) return group;
  const task = group?.tasksById[taskId];
  if (!optionId) return task;
  const option = task?.optionsById[optionId];
  return lineItemId ? option?.lineItemsById[lineItemId] : option;
};

const getCurrentGalleryPhotoOccurrence = (
  scanRoot: ScanRootOptimized,
  source: GalleryPhotoOccurrence,
): GalleryPhotoOccurrence | undefined => {
  const photo = getGalleryScanPartOrUndefined(scanRoot, source.context)
    ?.photos?.find(currentPhoto => currentPhoto.photoId === source.photo.photoId);
  return photo && { ...source, photo };
};

const updateScanPartPhotos = (
  scanRoot: ScanRootOptimized,
  context: ScanPartContext,
  photos: ScanPhoto[] | undefined,
): ScanRootOptimized =>
  getScanWithUpdatedScanPart(
    scanRoot,
    context.groupId,
    context.taskId,
    context.optionId,
    context.lineItemId,
    { photos },
  );

const removeGalleryPhotoOccurrences = (
  scanRoot: ScanRootOptimized,
  sources: readonly GalleryPhotoOccurrence[],
  exceptContext?: ScanPartContext,
): ScanRootOptimized =>
  sources.reduce((updatingScan, source) => {
    if (exceptContext && contextsAreEqual(source.context, exceptContext)) return updatingScan;
    return updateScanPartPhotos(
      updatingScan,
      source.context,
      getScanPhotosWithoutPhotoId(
        getGalleryScanPart(updatingScan, source.context).photos,
        source.photo.photoId,
      ),
    );
  }, scanRoot);

const addGalleryPhotos = (
  scanRoot: ScanRootOptimized,
  target: ScanPartContext,
  photos: readonly ScanPhoto[],
): ScanRootOptimized => {
  if (!photos.length) return scanRoot;
  const targetPhotos = getGalleryScanPart(scanRoot, target).photos;
  const targetPhotoIds = new Set(targetPhotos?.map(photo => photo.photoId));
  return updateScanPartPhotos(
    scanRoot,
    target,
    getUniqueScanPhotos(targetPhotos, photos.filter(photo => !targetPhotoIds.has(photo.photoId))),
  );
};

/**
 * Rewrites when a photo was taken, on every scan part that holds it. A photo copied to several
 * parts is still one photo, so its date reads the same everywhere it appears.
 */
export const setGalleryPhotoDate = (
  scanRoot: ScanRootOptimized,
  photoId: string,
  addedAt: number,
): ScanRootOptimized =>
  (scanRoot.optimized.allPhotoIdUsesByPhotoId[photoId] ?? []).reduce(
    (updatingScan, use) =>
      updateScanPartPhotos(
        updatingScan,
        use,
        getGalleryScanPart(updatingScan, use).photos?.map(photo => photo.photoId === photoId ? { ...photo, addedAt } : photo),
      ),
    scanRoot,
  );

/** Identifies one photo assignment within a specific scan part. */
export const getGalleryPhotoOccurrenceKey = ({
  photoId,
  ...context
}: ScanPartContext & { photoId: string }): string =>
  JSON.stringify([
    photoId,
    getGalleryPhotoContextKey(context),
  ]);

/** Applies one atomic Gallery assignment operation to a scan. */
export const applyGalleryPhotoOperation = (
  scanRoot: ScanRootOptimized,
  operation: GalleryPhotoOperation,
): ScanRootOptimized => {
  const sources = operation.sources
    .map(source => getCurrentGalleryPhotoOccurrence(scanRoot, source))
    .filter(source => source !== undefined);
  if (!sources.length) return scanRoot;
  if (operation.type === 'remove') {
    return removeGalleryPhotoOccurrences(scanRoot, sources);
  }
  if (operation.type === 'setDate') {
    return sources.reduce((updatingScan, source) => setGalleryPhotoDate(updatingScan, source.photo.photoId, operation.addedAt), scanRoot);
  }
  if (!getGalleryScanPartOrUndefined(scanRoot, operation.target)) return scanRoot;
  return addGalleryPhotos(
    operation.type === 'move'
      ? removeGalleryPhotoOccurrences(scanRoot, sources, operation.target)
      : scanRoot,
    operation.target,
    sources.map(source => source.photo),
  );
};

const buildGroupPhotoTargets = (
  group: ScanRootOptimized['groups'][number],
): GalleryPhotoTarget[] => {
  const context = { groupId: group.id };
  const title = groupNamePresenter(group);
  return [
    { key: getGalleryPhotoContextKey(context), context, title, depth: 0 },
    ...group.tasks.map(task => {
      const taskContext = { groupId: group.id, taskId: task.id };
      return {
        key: getGalleryPhotoContextKey(taskContext),
        context: taskContext,
        title: taskNamePresenter(task),
        subtitle: title,
        depth: 1 as const,
      };
    }),
  ];
};

/** Builds the complete scan-part hierarchy available as Gallery destinations. */
export const buildGalleryPhotoTargets = (scanRoot: ScanRootOptimized): GalleryPhotoTarget[] => [
  { key: getGalleryPhotoContextKey({}), context: {}, title: 'Curbside Photo', depth: 0 },
  ...scanRoot.groups.flatMap(buildGroupPhotoTargets),
];
